import { useMutation } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

import { useInvalidateIglooStaffConnection } from './get-igloo-staff-connection'
import { useInvalidateIglooStaffDevices } from './get-igloo-staff-devices'
import {
  IglooAssignedDoor,
  normalizeIglooAssignmentResult,
  RawIglooAssignmentResult,
} from './normalize-igloo-staff-devices'

type UpdateIglooStaffDevicesRequest = { buildingId: string; userId: string }

export type UpdateIglooStaffDevicesPayload = {
  // The FULL desired set of device ids for this building — not a delta. The backend diffs it
  // against what the member currently holds: remove, then add, then re-issue kept doors.
  devices: string[]
  // Unix ms, must be in the future and within 365 days.
  //
  // Send this ONLY when the user actually changed the expiration. Including it re-issues every
  // KEPT door with the new end date, which mints fresh Igloo eKeys — and Igloo eKeys cannot be
  // recalled and count against the property quota. Omitting it leaves kept doors untouched and
  // lets newly-added doors inherit the member's shortest current expiry.
  expiresAt?: number
}

const updateIglooStaffDevices = async (
  data: UpdateIglooStaffDevicesRequest,
  payload: UpdateIglooStaffDevicesPayload,
): Promise<IglooAssignedDoor[]> => {
  const response = await authAPI.put<RawIglooAssignmentResult>(
    `/igloowork/building/${data.buildingId}/staff/${data.userId}/devices`,
    {
      devices: payload.devices,
      ...(typeof payload.expiresAt === 'number' ? { expiresAt: payload.expiresAt } : {}),
    },
  )
  // The response is the persisted state AFTER the diff, and it is the only signal the caller gets
  // about per-door failures. Two shapes are live: before chitowntrainer-sails#6346 a failed door
  // was audit-logged and omitted from this array; after it the door stays and carries
  // `result: 'failed'` with a `reason` and a `held` flag. Callers must handle both — see
  // getFailedDoorIds.
  return normalizeIglooAssignmentResult(response.data)
}

export const useUpdateIglooStaffDevices = (data: UpdateIglooStaffDevicesRequest) => {
  const invalidateStaffDevices = useInvalidateIglooStaffDevices(data.userId)
  const invalidateStaffConnection = useInvalidateIglooStaffConnection(data.userId)
  return useMutation({
    mutationFn: (payload: UpdateIglooStaffDevicesPayload) => updateIglooStaffDevices(data, payload),
    onSuccess: () => {
      invalidateStaffDevices()
      invalidateStaffConnection()
    },
  })
}
